import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { colors, inputs, typography } from '../theme/tokens';
import Button from '../components/Button';
import { useNavigate, useParams } from 'react-router-dom';
import { TitleLarge } from '../components/Typography';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../config/supabase';
import { CreateMealPlanFormData, MealType } from '../types/mealPlan';
import { getCloudinaryConfig } from '../config/cloudinary';
import { generateSignature } from '../utils/cloudinary';
import noImage from '../assets/no-image.png';
import { Modal } from '../components/Modal';


const PageContainer = styled.div`
  padding: 1rem;
  width: 100%;
  box-sizing: border-box;
`;

const ContentContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const Header = styled.div`
  padding: 2rem;
  border-bottom: 1px solid ${colors.neutral.offWhite};
  background-color: ${colors.neutral.white};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 1rem;
`;

const HeaderTitle = styled(TitleLarge)`
  color: ${colors.brand.laurel};
  margin-bottom: 0.5rem;
`;

const FormContainer = styled.div`
  padding: 1rem;
  background-color: ${colors.neutral.white};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 100%;
  box-sizing: border-box;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Label = styled.label`
  font-family: ${typography.label.medium.fontFamily};
  font-size: ${typography.label.medium.fontSize};
  color: ${colors.neutral.darkCharcoal};
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  background: ${inputs.default.background};
  border: ${inputs.default.border};
  color: ${inputs.default.color};
  padding: ${inputs.sizes.default.padding};
  font-size: ${inputs.sizes.default.fontSize};
  border-radius: ${inputs.sizes.default.borderRadius};
  width: 100%;
  font-family: ${typography.paragraph.medium.fontFamily};
  margin-bottom: 0 !important;

  &::placeholder {
    color: ${inputs.default.placeholder};
  }

  &:focus {
    outline: none;
    border-color: ${inputs.default.focusBorder};
  }

  &:disabled {
    background-color: ${inputs.default.disabledBackground};
    color: ${inputs.default.disabledColor};
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  background: ${inputs.default.background};
  border: ${inputs.default.border};
  color: ${inputs.default.color};
  padding: ${inputs.sizes.default.padding};
  font-size: ${inputs.sizes.default.fontSize};
  border-radius: ${inputs.sizes.default.borderRadius};
  width: 100%;
  min-height: 100px;
  font-family: ${typography.paragraph.medium.fontFamily};
  resize: vertical;
  margin-bottom: 0 !important;

  &::placeholder {
    color: ${inputs.default.placeholder};
  }

  &:focus {
    outline: none;
    border-color: ${inputs.default.focusBorder};
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  label {
    cursor: pointer;
    font-family: ${typography.paragraph.medium.fontFamily};
    font-size: ${typography.paragraph.medium.fontSize};
    color: ${colors.neutral.charcoal};
  }

  input[type="checkbox"] {
    width: 1rem;
    height: 1rem;
    accent-color: ${colors.brand.laurel};
    cursor: pointer;
  }
`;

const DurationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DurationInput = styled(Input)`
  width: 100px;
  margin-bottom: 0;
`;

const DurationLabel = styled.span`
  font-family: ${typography.paragraph.medium.fontFamily};
  font-size: ${typography.paragraph.medium.fontSize};
  color: ${colors.neutral.charcoal};
`;

const ImageUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ImagePreviewContainer = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px dashed ${colors.neutral.lightCharcoal};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${colors.brand.laurel};
  }
`;

const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UploadButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  align-self: flex-start;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ErrorMessage = styled.div`
  color: ${colors.reds.bright};
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${colors.neutral.lightCharcoal};
  border-top-color: ${colors.brand.laurel};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const CreateMealPlan: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<CreateMealPlanFormData>({
    title: '',
    description: '',
    includeBreakfast: false,
    includeLunch: false,
    includeDinner: false,
    duration: '',
    coverImage: ''
  });
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [originalMealTypes, setOriginalMealTypes] = useState<{ breakfast: boolean; lunch: boolean; dinner: boolean }>({ breakfast: false, lunch: false, dinner: false });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'delete' | 'warning' | null>(null);
  const [pendingMealType, setPendingMealType] = useState<MealType | null>(null);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setFormData(prev => ({
      ...prev,
      duration: value
    }));
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);

    if (file.size > 10 * 1024 * 1024) {
      setUploadError('Image size must be less than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedImage(reader.result as string);
    };
    reader.readAsDataURL(file);

    setIsUploading(true);
    try {
      const timestamp = Math.round(new Date().getTime() / 1000);
      const signature = await generateSignature(timestamp);
      const cloudinaryConfig = getCloudinaryConfig();

      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', cloudinaryConfig.apiKey);
      formData.append('timestamp', timestamp.toString());
      formData.append('signature', signature);
      
      const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudinaryConfig.cloudName}/image/upload`;
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const data = await response.json();
      
      setFormData(prev => ({
        ...prev,
        coverImage: data.secure_url
      }));
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Failed to upload image. Please try again.');
      setSelectedImage(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    navigate('/meal-plans');
  };

  const handleMealTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    const mealType = name === 'includeBreakfast' ? 'breakfast' : name === 'includeLunch' ? 'lunch' : 'dinner';
    if (!checked && originalMealTypes[mealType as MealType]) {
      // Deselecting a meal type that was originally present
      setPendingMealType(mealType as MealType);
      setModalType('delete');
      setModalOpen(true);
    } else {
      setFormData(prev => ({ ...prev, [name]: checked }));
      // If this was the last checked, show warning
      if (!checked &&
        ((name === 'includeBreakfast' && !formData.includeLunch && !formData.includeDinner) ||
        (name === 'includeLunch' && !formData.includeBreakfast && !formData.includeDinner) ||
        (name === 'includeDinner' && !formData.includeBreakfast && !formData.includeLunch))) {
        setModalType('warning');
        setModalOpen(true);
      }
    }
  };

  useEffect(() => {
    const fetchMealPlan = async () => {
      if (!id) return;

      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('meal_plans')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;

        if (data) {
          setFormData({
            title: data.title,
            description: data.description,
            includeBreakfast: false,
            includeLunch: false,
            includeDinner: false,
            duration: '',
            coverImage: data.cover_image || ''
          });
          setSelectedImage(data.cover_image);
          setIsEditing(true);

          // Get the meal plan weeks to determine duration
          const { data: weeks } = await supabase
            .from('meal_plan_weeks')
            .select('id')
            .eq('meal_plan_id', id);

          if (weeks) {
            setFormData(prev => ({
              ...prev,
              duration: weeks.length.toString()
            }));
          }

          // Get the meal types from the first day of the first week
          if (weeks && weeks.length > 0) {
            const { data: firstDay } = await supabase
              .from('meal_plan_days')
              .select('id')
              .eq('week_id', weeks[0].id)
              .limit(1)
              .single();

            if (firstDay) {
              const { data: meals } = await supabase
                .from('meal_plan_meals')
                .select('meal_type')
                .eq('day_id', firstDay.id);

              if (meals) {
                const mealTypes = [...new Set(meals.map(m => m.meal_type))];
                setOriginalMealTypes({
                  breakfast: mealTypes.includes('breakfast'),
                  lunch: mealTypes.includes('lunch'),
                  dinner: mealTypes.includes('dinner'),
                });
                setFormData(prev => ({
                  ...prev,
                  includeBreakfast: mealTypes.includes('breakfast'),
                  includeLunch: mealTypes.includes('lunch'),
                  includeDinner: mealTypes.includes('dinner')
                }));
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching meal plan:', error);
        showToast('Failed to load meal plan for editing', 'error');
        navigate('/meal-plans');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMealPlan();
  }, [id, navigate, showToast]);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    if (!user) {
      showToast('You must be logged in to create a meal plan', 'error');
      return;
    }

    if (!formData.title.trim()) {
      showToast('Please enter a title for your meal plan', 'error');
      return;
    }

    if (!formData.includeBreakfast && !formData.includeLunch && !formData.includeDinner) {
      showToast('Please select at least one meal type', 'error');
      return;
    }

    const duration = parseInt(formData.duration);
    if (!duration || duration <= 0) {
      showToast('Please enter a valid duration (greater than 0)', 'error');
      return;
    }

    if (!formData.coverImage) {
      showToast('Please upload a cover image for your meal plan', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      // Get the creator's ID from CreatorDetails using the auth user's ID
      const { data: creatorDetails, error: creatorError } = await supabase
        .from('CreatorDetails')
        .select('id')
        .eq('AuthID', user.id)
        .single();

      if (creatorError) {
        throw new Error('Failed to verify creator account');
      }

      if (!creatorDetails) {
        throw new Error('No creator account found for the current user');
      }

      const creatorId = creatorDetails.id;

      let mealPlan;
      if (isEditing) {
        // Update existing meal plan
        const { data, error: mealPlanError } = await supabase
          .from('meal_plans')
          .update({
            title: formData.title.trim(),
            description: formData.description.trim(),
            cover_image: formData.coverImage,
            updated_at: new Date().toISOString()
          })
          .eq('id', id)
          .select()
          .single();

        if (mealPlanError) throw mealPlanError;
        if (!data) throw new Error('Failed to update meal plan');
        mealPlan = data;
      } else {
        // Create new meal plan
        const { data, error: mealPlanError } = await supabase
          .from('meal_plans')
          .insert([{
            user_id: user.id,
            title: formData.title.trim(),
            description: formData.description.trim(),
            cover_image: formData.coverImage,
            is_default: false,
            shared_code: null,
            is_shared: false,
            copied_from_plan_id: null,
            updated_at: null,
            archived_at: null,
            creator_id: creatorId
          }])
          .select()
          .single();

        if (mealPlanError) throw mealPlanError;
        if (!data) throw new Error('Failed to create meal plan');
        mealPlan = data;
      }

      if (!isEditing) {
        // Only create weeks, days, and meals for new meal plans
        // Step 2: Create meal plan weeks
        const weeksData = Array.from({ length: duration }, (_, index) => ({
          meal_plan_id: mealPlan.id,
          week_number: index + 1,
          start_date: null
        }));

        const { data: weeks, error: weeksError } = await supabase
          .from('meal_plan_weeks')
          .insert(weeksData)
          .select();

        if (weeksError) throw weeksError;
        if (!weeks || weeks.length === 0) throw new Error('Failed to create meal plan weeks');

        // Step 3: Create meal plan days (7 days per week, starting Sunday = 0)
        const daysData: any[] = [];
        weeks.forEach(week => {
          for (let dayOfWeek = 0; dayOfWeek <= 6; dayOfWeek++) { // Sunday = 0, Saturday = 6
            daysData.push({
              week_id: week.id,
              day_of_week: dayOfWeek,
              date: null
            });
          }
        });

        const { data: days, error: daysError } = await supabase
          .from('meal_plan_days')
          .insert(daysData)
          .select();

        if (daysError) throw daysError;
        if (!days || days.length === 0) throw new Error('Failed to create meal plan days');

        // Step 4: Create meal plan meals
        const selectedMeals: MealType[] = [];
        if (formData.includeBreakfast) selectedMeals.push('breakfast');
        if (formData.includeLunch) selectedMeals.push('lunch');
        if (formData.includeDinner) selectedMeals.push('dinner');

        const mealsData: any[] = [];
        days.forEach(day => {
          selectedMeals.forEach(mealType => {
            mealsData.push({
              day_id: day.id,
              meal_type: mealType,
              recipe_id: null,
              portions_override: null,
              reminder_time: null
            });
          });
        });

        const { error: mealsError } = await supabase
          .from('meal_plan_meals')
          .insert(mealsData);

        if (mealsError) throw mealsError;
      } else {
        // 1. Determine added and removed meal types
        const added: MealType[] = [];
        const removed: MealType[] = [];
        if (formData.includeBreakfast && !originalMealTypes.breakfast) added.push('breakfast');
        if (formData.includeLunch && !originalMealTypes.lunch) added.push('lunch');
        if (formData.includeDinner && !originalMealTypes.dinner) added.push('dinner');
        if (!formData.includeBreakfast && originalMealTypes.breakfast) removed.push('breakfast');
        if (!formData.includeLunch && originalMealTypes.lunch) removed.push('lunch');
        if (!formData.includeDinner && originalMealTypes.dinner) removed.push('dinner');

        // 2. Add new meal_plan_meals for added types (only if not already present)
        if (added.length > 0) {
          // Fetch all days for this meal plan
          const { data: weeks } = await supabase
            .from('meal_plan_weeks')
            .select('id')
            .eq('meal_plan_id', mealPlan.id);
          if (weeks) {
            const weekIds = weeks.map(w => w.id);
            const { data: days } = await supabase
              .from('meal_plan_days')
              .select('id')
              .in('week_id', weekIds);
            if (days) {
              // Fetch all existing meal_plan_meals for these days and added types
              const { data: existingMeals } = await supabase
                .from('meal_plan_meals')
                .select('day_id, meal_type')
                .in('day_id', days.map(d => d.id))
                .in('meal_type', added);
              const mealsData: any[] = [];
              days.forEach(day => {
                added.forEach(mealType => {
                  const alreadyExists = existingMeals?.some(
                    m => m.day_id === day.id && m.meal_type === mealType
                  );
                  if (!alreadyExists) {
                    mealsData.push({
                      day_id: day.id,
                      meal_type: mealType,
                      recipe_id: null,
                      portions_override: null,
                      reminder_time: null
                    });
                  }
                });
              });
              if (mealsData.length > 0) {
                await supabase.from('meal_plan_meals').insert(mealsData);
              }
            }
          }
        }
        // 3. Delete meal_plan_meals for removed types
        for (const mealType of removed) {
          // Fetch all week ids for this meal plan
          const { data: weeks } = await supabase
            .from('meal_plan_weeks')
            .select('id')
            .eq('meal_plan_id', mealPlan.id);
          const weekIds = weeks?.map(w => w.id) || [];
          // Fetch all day ids for these weeks
          const { data: days } = await supabase
            .from('meal_plan_days')
            .select('id')
            .in('week_id', weekIds);
          const dayIds = days?.map(d => d.id) || [];
          // Delete all meal_plan_meals for this meal type and these days
          await supabase
            .from('meal_plan_meals')
            .delete()
            .eq('meal_type', mealType)
            .in('day_id', dayIds);
        }
      }

      showToast(isEditing ? 'Meal plan updated successfully!' : 'Meal plan created successfully!', 'success');
      navigate(`/meal-plans/${mealPlan.id}`);

    } catch (error) {
      console.error('Error creating meal plan:', error);
      showToast('Something went wrong while creating your meal plan. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <ContentContainer>
        <Header>
          <HeaderContent>
            <HeaderTitle>{isEditing ? 'Edit Meal Plan' : 'Create Meal Plan'}</HeaderTitle>
          </HeaderContent>
          <HeaderButtons>
            <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={() => handleSubmit()} disabled={isSubmitting || (!formData.includeBreakfast && !formData.includeLunch && !formData.includeDinner)}>
              {isSubmitting ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Plan' : 'Create Plan')}
            </Button>
          </HeaderButtons>
        </Header>
        
        <FormContainer>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="title">What do you want to call your plan?</Label>
              <Input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter plan name..."
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="description">Description (optional)</Label>
              <TextArea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter a description for your meal plan..."
              />
            </FormGroup>

            <FormGroup>
              <Label>What meals do you want to include?</Label>
              <CheckboxGroup>
                <Checkbox>
                  <input
                    type="checkbox"
                    id="includeBreakfast"
                    name="includeBreakfast"
                    checked={formData.includeBreakfast}
                    onChange={handleMealTypeChange}
                  />
                  <label htmlFor="includeBreakfast">Breakfast</label>
                </Checkbox>
                <Checkbox>
                  <input
                    type="checkbox"
                    id="includeLunch"
                    name="includeLunch"
                    checked={formData.includeLunch}
                    onChange={handleMealTypeChange}
                  />
                  <label htmlFor="includeLunch">Lunch</label>
                </Checkbox>
                <Checkbox>
                  <input
                    type="checkbox"
                    id="includeDinner"
                    name="includeDinner"
                    checked={formData.includeDinner}
                    onChange={handleMealTypeChange}
                  />
                  <label htmlFor="includeDinner">Dinner</label>
                </Checkbox>
              </CheckboxGroup>
            </FormGroup>

            {!isEditing && (
              <FormGroup>
                <Label htmlFor="duration">How long is this meal plan?</Label>
                <DurationContainer>
                  <DurationInput
                    type="text"
                    id="duration"
                    name="duration"
                    value={formData.duration}
                    onChange={handleDurationChange}
                    placeholder="4"
                  />
                  <DurationLabel>weeks</DurationLabel>
                </DurationContainer>
              </FormGroup>
            )}

            <FormGroup>
              <Label>Cover Image</Label>
              <ImageUploadContainer>
                <ImagePreviewContainer onClick={handleImageClick}>
                  {selectedImage ? (
                    <ImagePreview src={selectedImage} alt="Cover preview" />
                  ) : (
                    <ImagePreview src={noImage} alt="No image" />
                  )}
                  {isUploading && (
                    <LoadingSpinner />
                  )}
                </ImagePreviewContainer>
                <UploadButton variant="outline" onClick={handleImageClick} disabled={isUploading}>
                  {isUploading ? 'Uploading...' : 'Upload Cover Image'}
                </UploadButton>
                <HiddenFileInput
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                {uploadError && <ErrorMessage>{uploadError}</ErrorMessage>}
              </ImageUploadContainer>
            </FormGroup>
          </Form>
        </FormContainer>
      </ContentContainer>
      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          if (modalType === 'delete' && pendingMealType) {
            // Revert checkbox
            setFormData(prev => ({ ...prev, [`include${pendingMealType.charAt(0).toUpperCase() + pendingMealType.slice(1)}`]: true }));
            setPendingMealType(null);
          }
        }}
        title={modalType === 'delete' ? 'Remove Meal Type?' : 'At Least One Meal Type Required'}
      >
        {modalType === 'delete' ? (
          <>
            <p>Removing this meal type will delete all existing records for this meal type in this meal plan. Are you sure?</p>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
              <Button onClick={() => {
                setModalOpen(false);
                if (pendingMealType) {
                  setFormData(prev => ({ ...prev, [`include${pendingMealType.charAt(0).toUpperCase() + pendingMealType.slice(1)}`]: false }));
                  setPendingMealType(null);
                }
              }} color="danger">Yes, Remove</Button>
              <Button variant="outline" onClick={() => {
                setModalOpen(false);
                if (pendingMealType) {
                  setFormData(prev => ({ ...prev, [`include${pendingMealType.charAt(0).toUpperCase() + pendingMealType.slice(1)}`]: true }));
                  setPendingMealType(null);
                }
              }}>Cancel</Button>
            </div>
          </>
        ) : (
          <>
            <p>At least one meal type is required for a meal plan.</p>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
              <Button onClick={() => setModalOpen(false)}>OK</Button>
            </div>
          </>
        )}
      </Modal>
    </PageContainer>
  );
};

export default CreateMealPlan; 